import { useState, useContext, useMemo } from "react";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import ListGroup from "react-bootstrap/ListGroup";

import { useParams, Link } from "react-router-dom";

import {
  useQuery,
  NetworkStatus
} from "@apollo/client";

import { 
  format,
  formatDistanceToNowStrict,
  sub,
  isAfter,
  add,
  isToday,
  isYesterday
} from "date-fns";

import { 
  SINGLE_CHARGER,
  SINGLE_CHARGER_STATUS_MESSAGES,
  CHARGER_UPTIME,
} from "./gql";

import { 
  Alert,
  Skeleton,
  StatusBadge,
  Button,
  UptimeChart
} from "@evos/components";

import Header from "components/Header";
import ChargerProgressBar from "./sub-components/ChargerProgressBar";
import ChargerCommands from "./sub-components/ChargerCommands";
import { InfiniteScrollingList } from "@evos/components";

import { constructLocationString } from "lib/functions/charger";
import { AuthContext } from "lib/context/auth";
import { floorToThreeDecimals, roundToTwoDecimals } from "lib/functions/general";
import { useWindowSize } from "@evos/hooks";
import ampolAccountIds from "lib/constants/ampolAccountIds.json";

import { 
  ChargersConnection,
  ChargerMonitors
} from "types";

function useAuth() {
  return useContext(AuthContext);
}

function formatStatusMessageTimestamp(isoTimestamp: string | null | undefined): string {
  if (isoTimestamp == null || isoTimestamp === "") return "No timestamp";
  try {
    const d = new Date(isoTimestamp);
    if (Number.isNaN(d.getTime())) return isoTimestamp;
    if (isToday(d)) return `Today, ${format(d, "HH:mm:ss.SS")}`;
    if (isYesterday(d)) return `Yesterday, ${format(d, "HH:mm:ss.SS")}`;
    return format(d, "dd MMM yyyy, HH:mm:ss.SS");
  } catch {
    return isoTimestamp;
  }
}

export default function ChargerDetail() {
  const params = useParams();
  const auth = useAuth();
  const darkMode = auth?.darkMode ?? false;
  const serial = params.serial ?? "";
  const windowWidth = useWindowSize().width ?? window.innerWidth;
  const userRoles = auth?.user?.roles ?? [];

  const hourAgoTimestamp = useMemo(() => (sub(new Date(), { minutes: 60 }).toISOString()), []);

  const { 
    data: chargerData, 
    networkStatus: chargerNetworkStatus,
    error: chargerDataError
  } = useQuery<ChargersConnection>(
    SINGLE_CHARGER,
    { 
      variables: { 
        chargers_n: 1,
        where: {
          serial: {
            eq: serial
          }
        },
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
      pollInterval: 60000
    }
  );

  const chargerNode = chargerData?.chargers?.edges[0]?.node;
  const chargerDataInitialLoading = chargerNetworkStatus === NetworkStatus.loading;

  const {
    data: statusMessagesQueryData,
    networkStatus: statusMessagesNetworkStatus,
    error: statusMessagesError,
    fetchMore: fetchMoreStatusMessages,
    refetch: refetchStatusMessages
  } = useQuery<ChargerMonitors>(SINGLE_CHARGER_STATUS_MESSAGES, {
    variables: {
      chargers_where: {
        serial: {
          eq: params.serial
        }
      },
      before: hourAgoTimestamp,
      n: 20,
      after: null
    },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    errorPolicy: "all",
    skip: serial == null,
    pollInterval: 6000
  });

  const chargerStatusMessagesInitialLoading = statusMessagesNetworkStatus === 1;
  const chargerStatusMessagesFetchMoreLoading = statusMessagesNetworkStatus === 3;
  const chargerStatusMessagesRefetchLoading = statusMessagesNetworkStatus === 4;

  const pageInfoObject = statusMessagesQueryData?.charger_monitors?.charger_status_messages?.pageInfo;

  const chargerStatusMessagesList = statusMessagesQueryData?.charger_monitors?.charger_status_messages?.edges?.map(d => ({
    timestamp: formatStatusMessageTimestamp(d?.node?.receiveTimestamp ?? undefined),
    errorCode: d?.node?.errorCode ?? "No error",
    vendorErrorCode: d?.node?.vendorErrorCode ?? "No error",
    status: d?.node?.status != null ? <><StatusBadge status={d?.node?.status} /> {d?.node?.status}</> : "No status",
    info: d?.node?.info ?? "No info",
  }));

  const handleFetchMore = (pageInfo: ChargerMonitors["charger_monitors"]["charger_status_messages"]["pageInfo"]) => {
    if (pageInfo?.hasNextPage === true) {
      fetchMoreStatusMessages({
        variables: {
          n: 20,
          after: pageInfo.endCursor,
        }
      });
    }
  };

  const [ chargerUptimeDataLoading, setChargerUptimeDataLoading ] = useState(true);

  const {
    data: chargerUptimeQueryData,
    error: chargerUptimeDataError,
    refetch: refetchSiteUptime
  } = useQuery<ChargersConnection>(CHARGER_UPTIME, {
    variables: {
      n: 1,
      where: {
        serial: {eq: params.serial}
      },
    },
    onCompleted: (data) => {
      function refetchAfterTimeout() {
        setTimeout(() => {
          refetchSiteUptime();
        }, 5000);
      }

      if (data != null) {
        if (data.chargers?.edges[0]?.node?.uptime == null){
        // site_uptime will be null if it is currently being calculated
          refetchAfterTimeout();
        } else {
          const timestampNow = new Date();
          const timestampLastUpdate = new Date(data.chargers?.edges[0]?.node?.uptime?.calculation_update_timestamp);
          const updateIntervalInMinutes = data.chargers?.edges[0]?.node?.uptime?.calculation_update_interval;
          if (timestampLastUpdate != null && updateIntervalInMinutes != null && isAfter(timestampNow, add(timestampLastUpdate, {minutes: updateIntervalInMinutes}))) {
            // Refetch the uptime data if the update interval has passed
            refetchAfterTimeout();
          } else {
            setChargerUptimeDataLoading(false);
          }
        }
      }
    },
    onError: () => {
      setChargerUptimeDataLoading(false);
    },
    fetchPolicy: "cache-and-network",
    skip: serial == null || chargerData == null
  });

  const uptimeData = chargerUptimeQueryData?.chargers?.edges?.[0]?.node?.uptime ?? undefined;

  // function downloadPdf(base64: string, filename: string) {
  //   const source = "data:application/pdf;base64,"+base64;
  //   const downloadLink = document.createElement("a");

  //   downloadLink.href = source;
  //   downloadLink.download = filename;
  //   downloadLink.click();
  // }

  // PDF Test Reports
  // const [getPdf, { loading: getPdfLoading }] = useLazyQuery(GET_TEST_REPORT_PDF, {
  //   variables: { serial: serial},
  //   context: {clientName: "testReport"},
  //   onCompleted: (data) => {
  //     downloadPdf(data?.pdf_by_serial_gitlab?.base64, serial + "_test_report.pdf");
  //   },
  //   notifyOnNetworkStatusChange: true
  // });

  // Location
  const locationObject = {
    street_address: chargerNode?.salesforce_details?.street_address ?? "",
    suburb: chargerNode?.salesforce_details?.suburb ?? "",
    city: chargerNode?.salesforce_details?.city ?? "",
    province: chargerNode?.salesforce_details?.province ?? "",
    postcode: chargerNode?.salesforce_details?.postcode ?? "",
    country: chargerNode?.salesforce_details?.country ?? ""
  };
  const locationString = ((chargerData != null) &&
    (chargerNode?.salesforce_details != null) &&
    constructLocationString(locationObject)) || undefined;

  const listGroupStyle = {
    backgroundColor: darkMode ? "#212529" : "",
    color: darkMode ? "white" : "inherit",
    borderBottom: darkMode ? "1px solid white" : "1px solid lightgrey",
    padding: "10px 20px"
  };

  const progressBarValue = () => {
    const installed = chargerNode?.salesforce_details?.installation_ref != null;
    const adopted = chargerNode?.salesforce_details?.owner_id !== "00598000000LAQkAAO";
    const connected = chargerNode?.salesforce_details?.last_comms != null;
    if (installed && adopted && connected) {
      return 100;
    } else if (installed && adopted) {
      return 50;
    } else if (installed) {
      return 4;
    } else return 0;
  };

  const graphanaChargerUrl = process.env.REACT_APP_GRAPHANA_CHARGER_URL != null
    ? `${process.env.REACT_APP_GRAPHANA_CHARGER_URL}?orgId=1&var-chargers=${serial}`
    : undefined;

  return (
    <div className="ChargerDetail">
      <Header />
      <Container>
        <Row>
          <Col xs={12} className="mt-4 mb-2">
            <h4><Link to="/" className="fw-bold">&#171;Dashboard</Link> &#47;&#47; {params.serial} </h4>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <ChargerProgressBar
              darkMode={darkMode}
              progressBarValue={progressBarValue}
            />
          </Col>
        </Row>
        { chargerDataInitialLoading
          ? <Skeleton height="38px" outerClassName="d-flex mx-2 mt-4 mb-2" />
          : <ChargerCommands
            userRoles={userRoles}
            serial={serial}
            servicedById={chargerNode?.salesforce_details?.serviced_by}
          />
        }
        <Row className="d-flex pt-3 pb-4">
          <Col xs={12} lg={4} xxxl={4} className="mb-4 mb-md-0">
            <Card className={`text-start pb-4 px-0 ${darkMode ? "bg-dark" : ""}`} style={{height: "100%"}}>
              <Card.Title className="p-3 mb-0" as="h4">Charger Details</Card.Title>
              { (chargerDataError != null && chargerNode == null)
                ? <Alert className="mx-3" variant="danger" message="There was an error loading charger details data" />
                : <ListGroup variant="flush">
                  { !chargerDataInitialLoading
                    ? <>
                      <ListGroup.Item style={listGroupStyle}><b>Serial:</b> {chargerNode?.serial}</ListGroup.Item>
                      <ListGroup.Item style={listGroupStyle}><b>Name:</b> {chargerNode?.salesforce_details?.name}</ListGroup.Item>
                      <ListGroup.Item style={listGroupStyle}><b>Model:</b> {chargerNode?.salesforce_details?.model ?? "Unknown"}</ListGroup.Item>
                      <ListGroup.Item style={listGroupStyle}><b>Location:</b> {locationString}</ListGroup.Item>
                      <ListGroup.Item style={listGroupStyle}><b>Install date: </b> 
                        {chargerNode?.salesforce_details?.installation_date != null
                          ? format(new Date(chargerNode?.salesforce_details?.installation_date), "yyyy-MM-dd") 
                          : "Not Installed"}
                      </ListGroup.Item>
                      <ListGroup.Item style={listGroupStyle}><b>Circuit amperage limit:</b> {chargerNode?.salesforce_details?.circuit_amp_limit ?? "Unknown"}</ListGroup.Item>
                      <ListGroup.Item style={listGroupStyle}><b>Circuit phases:</b> {chargerNode?.salesforce_details?.circuit_phases ?? "Unknown"}</ListGroup.Item>
                      <ListGroup.Item style={listGroupStyle}><b>Mode:</b> {chargerNode?.salesforce_details?.mode ?? "Unknown"}</ListGroup.Item>
                      <ListGroup.Item style={listGroupStyle}><b>Owner ID:</b> {chargerNode?.salesforce_details?.owner_id}</ListGroup.Item> 
                      <ListGroup.Item style={listGroupStyle}><b>Serviced By ID:</b> {chargerNode?.salesforce_details?.serviced_by ?? "None"}</ListGroup.Item>
                      <ListGroup.Item style={listGroupStyle}><b>Salesforce Status:</b> {chargerNode?.salesforce_details?.status ?? "Unknown"}</ListGroup.Item>
                      <ListGroup.Item style={listGroupStyle}><b>IP Address:</b> {
                        (() => {
                          const vpnIp = (chargerNode?.salesforce_details as { vpn_ip?: string } | undefined)?.vpn_ip;
                          if (vpnIp != null && vpnIp !== "") {
                            return vpnIp;
                          }
                          return chargerNode?.vpn_status?.connections?.[0]?.allowed_ips?.split("/")[0] ?? "Unknown";
                        })()
                      }</ListGroup.Item> 
                      <ListGroup.Item style={listGroupStyle}><b>OCPP Server:</b> {chargerNode?.salesforce_details?.ocpp_server}</ListGroup.Item>
                      <ListGroup.Item style={listGroupStyle}><b>Last Comms: </b> 
                        {
                          (chargerNode?.salesforce_details?.last_comms != null) 
                            ? `${chargerNode?.salesforce_details?.last_comms} 
                            (${formatDistanceToNowStrict(new Date(chargerNode?.salesforce_details?.last_comms))} ago)`
                            : "Never"
                        }
                      </ListGroup.Item> 
                      <ListGroup.Item style={listGroupStyle}><b>Firmware Version:</b> {chargerNode?.salesforce_details?.firmware_version ?? "Unknown"}</ListGroup.Item>
                      <ListGroup.Item style={listGroupStyle}><b>OCPP Status:</b> <StatusBadge status={chargerNode?.salesforce_details?.ocpp_status ?? "Unknown"} /> {chargerNode?.salesforce_details?.ocpp_status ?? "Unknown"}</ListGroup.Item> 
                      <ListGroup.Item style={listGroupStyle}><b>OCPP Firmware Status:</b> {chargerNode?.salesforce_details?.ocpp_firmware_status ?? "None"}</ListGroup.Item>
                      { (ampolAccountIds[(process.env.REACT_APP_ENV as "staging" | "production")]) === chargerNode?.salesforce_details?.serviced_by
                        && <>
                          <ListGroup.Item style={listGroupStyle}><b>Ampol Installation Event Sent?:</b> {chargerNode?.salesforce_details?.ampol_installed_messaged === true ? "Yes" : "No"}</ListGroup.Item>
                          <ListGroup.Item style={listGroupStyle}><b>Ampol Installation Event Timestamp: </b> 
                            {
                              (chargerNode?.salesforce_details?.ampol_installed_messaged_timestamp != null) 
                                ? `${chargerNode?.salesforce_details?.ampol_installed_messaged_timestamp} 
                                (${formatDistanceToNowStrict(new Date(chargerNode?.salesforce_details?.ampol_installed_messaged_timestamp))} ago)`
                                : "Never Sent"
                            }
                          </ListGroup.Item>
                        </>
                      }
                    </>
                    : <Skeleton height="38px" lines={16} innerClassName="mb-2" outerClassName="mx-2" />
                  }
                </ListGroup>
              } 
              { graphanaChargerUrl != null && !chargerDataInitialLoading
                && <div className="mt-3 ms-3">
                  <Button href={graphanaChargerUrl} target="_blank">
                  View Charger in Grafana
                  </Button>
                </div>
              }
            </Card>
          </Col>
          <Col xs={12} lg={8} xxxl={8}>
            <Card className={`pb-3 ${darkMode ? "bg-dark" : ""}`} style={{overflow: "auto"}}>
              <Card.Title className="p-3 mb-0" as="h4">Status Messages</Card.Title>
              <div style={{minWidth: statusMessagesError == null ? "750px" : "0px"}}>
                <InfiniteScrollingList
                  data={chargerStatusMessagesList}
                  initialLoading={chargerStatusMessagesInitialLoading}
                  fetchMoreLoading={chargerStatusMessagesFetchMoreLoading}
                  refetchLoading={chargerStatusMessagesRefetchLoading}
                  pageSize={20}
                  error={statusMessagesError}
                  listItemWidths="0.25fr 0.12fr 0.12fr 0.13fr 0.38fr"
                  listItemHeight={60}
                  listHeight={815}
                  pageInfo={pageInfoObject}
                  windowWidth={windowWidth}
                  fetchMoreData={handleFetchMore}
                  refetchData={(n) => refetchStatusMessages({
                    n: Math.max(n, 100)
                  })}
                />
              </div>
            </Card>
          </Col>
        </Row>
        <Row className="pb-5">
          <Col md={4}>
            <Card className="p-3 d-flex justify-content-center" style={{height: "100%"}}>
              { !chargerDataInitialLoading
                ? <h3 className="fw-semibold text-primary mb-0" style={{fontSize: "1.5em"}}>
                  { (chargerNode?.total_energy ?? 0) >= 1000000
                    ? roundToTwoDecimals((chargerNode?.total_energy ?? 0) / 1000000)
                    : roundToTwoDecimals((chargerNode?.total_energy ?? 0) / 1000)
                  }
                  <span className="ps-1" style={{fontSize: "1.2rem"}}>
                    { (chargerNode?.total_energy ?? 0) >= 1000000  
                      ? "MWh"
                      : "kWh"
                    }
                  </span>
                </h3>
                : <Skeleton height="30px" width="40%" />
              }
              <p className="mb-0 text-secondary">Total Energy - Lifetime</p>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="p-3 d-flex justify-content-center" style={{height: "100%"}}>
              <div className="d-flex align-items-center justify-content-between flex-wrap">
                <div>
                  { !chargerDataInitialLoading && !chargerUptimeDataLoading
                    ? <h3 className="fw-semibold text-primary mb-0" style={{fontSize: "1.5em"}}>
                      { uptimeData?.calculation_value != null && chargerUptimeDataError == null
                        ? `${floorToThreeDecimals(Number(uptimeData.calculation_value))}%`
                        : "Unknown"
                      }
                    </h3>
                    : <Skeleton height="30px" width="100%" />  
                  }
                  <p className="mb-0 text-secondary">Uptime</p>
                </div>
                <UptimeChart
                  uptimeValue={floorToThreeDecimals(Number(uptimeData?.calculation_value))}
                  loading={chargerUptimeDataLoading}
                  error={chargerUptimeDataError}
                />
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}